import React from "react";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {addAnchorsToContent, generateTableOfContents} from "../helpers/generate-table-of-contents";
import {sanitizeHTML} from "../helpers/safe-html-renderer";
import {convertLexicalJsonToHtml} from "../helpers/lexical-to-html";
import ArticleTag from "./ui/article-tag";
import SpecialistProfileImage from "../../specialists/components/specialist-profile-image";
import AppButton from "@common/components/app-button";
import {convertLexicalJsonToPlainText} from "../helpers/lexical-to-plain-text";
import {ChevronRightIcon} from "@heroicons/react/24/solid";

interface ArticlePrintContentProps {
    article: ArticleDetails;
}

const ArticlePrintContent = React.forwardRef<HTMLDivElement, ArticlePrintContentProps>(({article}, ref) => {
    const tableOfContents = React.useMemo(() => {
        if (!article?.content) return [];

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML for TOC:', error);
        }

        return generateTableOfContents(htmlContent);
    }, [article?.content]);

    const safeTitle: string = React.useMemo(() => {
        if (!article?.title) return '';

        let htmlTitle = article.title;
        try {
            if (article.title.startsWith('{') || article.title.startsWith('[')) {
                htmlTitle = convertLexicalJsonToHtml(article.title);
            }
        } catch (error) {
            console.warn('Error converting title Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlTitle);
    }, [article?.title]);

    const safeSummary: string = React.useMemo(() => {
        if (!article?.summary) return '';

        let htmlSummary = article.summary;
        try {
            if (article.summary.startsWith('{') || article.summary.startsWith('[')) {
                htmlSummary = convertLexicalJsonToHtml(article.summary);
            }
        } catch (error) {
            console.warn('Error converting summary Lexical JSON to HTML:', error);
        }

        return sanitizeHTML(htmlSummary);
    }, [article?.summary]);

    const safeContent: string = React.useMemo(() => {
        if (!article?.content) return '';

        let htmlContent = article.content;
        try {
            if (article.content.startsWith('{') || article.content.startsWith('[')) {
                htmlContent = convertLexicalJsonToHtml(article.content);
            }
        } catch (error) {
            console.warn('Error converting Lexical JSON to HTML:', error);
        }

        const contentWithAnchors = addAnchorsToContent(htmlContent);
        return sanitizeHTML(contentWithAnchors);
    }, [article?.content]);

    return (
        <div
            ref={ref}
            className="print-only"
        >
            <div
                className="space-y-6"
                style={{
                    padding: '20px',
                    maxWidth: '210mm',
                    margin: '0 auto',
                    background: 'white',
                    color: '#000000',
                    display: 'block',
                    visibility: 'visible'
                }}>
                {article?.tags && article.tags.length > 0 && (
                    <div style={{marginBottom: '16px'}}>
                        {article.tags.map((tag, index) => (
                            <React.Fragment key={index}>
                                <ArticleTag text={tag}/>
                                {index < article.tags.length - 1 && <span style={{margin: '0 8px'}}>·</span>}
                            </React.Fragment>
                        ))}
                    </div>
                )}

                <h1
                    style={{
                        color: '#000000',
                        marginBottom: '16px',
                        fontSize: '24px',
                        fontWeight: 'bold',
                        display: 'block',
                        visibility: 'visible'
                    }}
                    dangerouslySetInnerHTML={{__html: safeTitle || 'Brak tytułu'}}
                />

                {safeSummary && safeSummary.trim() && (
                    <p
                        style={{
                            color: '#334155',
                            marginBottom: '16px',
                            fontSize: '16px',
                            lineHeight: '1.5',
                            display: 'block',
                            visibility: 'visible'
                        }}
                        dangerouslySetInnerHTML={{__html: safeSummary}}
                    />
                )}

                {tableOfContents.length > 0 && (
                    <ul style={{
                        marginBottom: '24px',
                        marginLeft: '20px',
                        listStyle: 'none',
                        display: 'block',
                        visibility: 'visible'
                    }}>
                        {tableOfContents.map((item, index) => (
                            <li key={index}
                                style={{marginBottom: '8px', fontSize: '16px', fontWeight: 'bold', display: 'block'}}>
                                <div style={{
                                    display: 'inline-block',
                                    width: '4px',
                                    height: '4px',
                                    backgroundColor: '#3b82f6',
                                    borderRadius: '50%',
                                    marginRight: '12px',
                                    verticalAlign: 'middle'
                                }}></div>
                                <span style={{color: '#3b82f6', display: 'inline'}}>{item.title}</span>
                            </li>
                        ))}
                    </ul>
                )}

                <div
                    style={{
                        color: '#334155',
                        lineHeight: '1.6',
                        fontSize: '16px',
                        display: 'block',
                        visibility: 'visible'
                    }}
                    dangerouslySetInnerHTML={{__html: safeContent || '<p>Brak treści</p>'}}
                />

                {article.exercises && article.exercises.length > 0 && (
                    <section className="[&>*]:!text-[#334155] space-y-1 border-t border-outline-mild border-solid pt-8">
                        <h2 className="rich-text-h2">
                            Rekomendowane ćwiczenia
                        </h2>
                        <p className="text-on-surface/70">
                            Zestaw ćwiczeń przedstawiony poniżej został przygotowany jako praktyczne
                            rozszerzenie treści artykułu. Ich zastosowanie powinno być każdorazowo
                            dostosowane do aktualnego stanu zdrowia i zaleceń prowadzącego specjalisty.
                        </p>
                        <ul className="list-disc pl-5 space-y-1">
                            {article.exercises.map((exercise) => (
                                <li key={exercise.id}>
                                    <strong>{exercise.title}</strong>
                                    {exercise.description && (
                                        <span>{`: ${exercise.description}`}</span>
                                    )}
                                    {exercise.url && (
                                        <span>
                                            {" "}
                                            <a
                                                href={exercise.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-link hover:text-link-hover"
                                            >
                                                Zobacz ćwiczenie
                                            </a>
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {article?.author && (
                    <section
                        className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                        <h2 className="rich-text-h2">
                            O autorze
                        </h2>
                        <div className="flex items-center gap-4">
                            <div className="shrink-0">
                                <SpecialistProfileImage
                                    specialistId={article.author.id}
                                    specialistName={article.author.fullName}
                                    className="w-16 h-16 !rounded-2xl"
                                    classNameImg="!rounded-2xl"
                                    forceLoad={true}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <h3 className="font-semibold text-lg text-[#161C1D] m-0 leading-6">
                                    {article.author.fullName}
                                </h3>
                                {article.author.specialization && (
                                    <p className="text-[#161C1D] text-base m-0 leading-5">
                                        {article.author.specialization}
                                    </p>
                                )}
                            </div>
                        </div>
                    </section>

                )}

                {article?.relatedArticles && article.relatedArticles.length > 0 && (
                    <section
                        className="[&>*]:!text-[#334155] border-t border-outline-mild border-solid flex flex-col gap-4 pt-6">
                        <h2 className="rich-text-h2">
                            Powiązane artykuły
                        </h2>
                        <ul className="flex flex-col gap-2 list-none p-0 m-0">
                            {article.relatedArticles.map((related) => (
                                <li key={related.id}>
                                    <AppButton
                                        classNameContentWrapper="[&>div]:w-full"
                                        className="w-full px-[14px] py-3 bg-white border border-outline-mild rounded-lg text-left hover:border-outline transition-colors duration-150 group">
                                        <div className="flex items-center justify-between w-full min-w-full">
                                            <span className="text-sm text-on-surface leading-snug truncate">
                                                {convertLexicalJsonToPlainText(related.title)}
                                            </span>
                                            <ChevronRightIcon className="print:hidden w-3.5 h-3.5 text-on-surface/30 flex-shrink-0 group-hover:text-on-surface/60 transition-colors"/>
                                        </div>
                                    </AppButton>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </div>
    );
});

export default ArticlePrintContent;
